/* eslint-disable @typescript-eslint/no-explicit-any */
import useTranslation from '@/hooks/i18n';
import {
  fetchKnowledgeHubPage,
  fetchLatestResourcesByTheme,
  fetchLatestResourcesByType,
  fetchLatestResourcesByTopic,
  fetchLatestResources,
  fetchTrendingResources,
  fetchAllTimePopularFavouriteResources,
} from '@/services/knowledge-hub/knowledge-hub.service';
import { KnowledgeHubPage, ResourceEntity, ThemeEntity, TopicEntity } from '@/graphql/generated/graphql';
import { create } from 'zustand';
import { compare } from '../utils/compare.utils';

type ResourcesMap = Record<string, ResourceEntity[] | null>;
type ResourcesMetaMap = Record<
  string,
  { meta: { pagination: { total: number; pageCount?: number; page?: number; pageSize?: number } } } | null
>;

type KnowledgeHubStore = {
  loading: boolean;
  loadingThemeContent: boolean;
  loadingLatestsByType: boolean;
  page: { page: KnowledgeHubPage | null };
  topics: TopicEntity[] | undefined;
  latestByTopic: ResourcesMap;
  latestByTheme: ResourcesMap;
  latestResources: ResourceEntity[];
  trendingResources: ResourceEntity[];
  latestArticles: ResourceEntity[];
  latestTopTips: ResourceEntity[];
  latestGuides: ResourceEntity[];
  trendingResourcesLoading: boolean;
  allTimeFavouriteResources: ResourceEntity[];
  latestByThemeMeta: ResourcesMetaMap;
  highlights: ResourcesMap;
  filterActive: string | null;
  resetFilter: () => void;
  onClickMoreTheme: (theme: ThemeEntity, pageNumber: number) => Promise<void>;
  onInit: (topicParam?: string, themeParam?: string) => Promise<void>;
  onInitMain: () => Promise<void>;
  t: (value: string) => string;
  loadHighlights: (page: { page: KnowledgeHubPage }) => ResourcesMap | undefined;
  filterByTheme: (theme: string, topic: TopicEntity) => void;
  loadTrendingResources: () => Promise<void>;
  loadLatests: () => Promise<void>;
};

export const useKnowledgeHubStore = create<KnowledgeHubStore>()((set, get) => ({
  loading: false,
  loadingThemeContent: false,
  loadingLatestsByType: false,
  filterActive: '',
  trendingResourcesLoading: false,
  page: { page: null },
  topics: [],
  latestResources: [],
  latestArticles: [],
  latestTopTips: [],
  latestGuides: [],
  trendingResources: [],
  allTimeFavouriteResources: [],
  latestByTopic: {},
  latestByTheme: {},
  latestByThemeMeta: {},
  highlights: {},
  t: useTranslation().t,
  resetFilter: () => {
    set({ filterActive: null });
    get().onInit();
  },
  onInitMain: async () => {
    set({ loading: true });
    const { loadHighlights } = get();

    const [page, latests, trending, allTimeFavourites] = await Promise.all([
      fetchKnowledgeHubPage(),
      fetchLatestResources(),
      fetchTrendingResources(2),
      fetchAllTimePopularFavouriteResources(),
    ]);

    if (!page?.page?.topics?.data) {
      throw new Error('Failed to fetch topics');
    }

    const highlights = page ? loadHighlights(page) : {};

    set({
      page,
      topics: page.page.topics.data,
      highlights,
      latestResources: latests?.resources || [],
      trendingResources: trending?.resources || [],
      allTimeFavouriteResources: allTimeFavourites?.resources || [],
      latestByTopic: {},
      latestByTheme: {},
      latestByThemeMeta: {},
      loading: false,
    });
  },
  onInit: async (topicParam?: string, themeParam?: string) => {
    const urlParams = new URLSearchParams(window.location.search);
    const pageNumber = urlParams.get('page');

    set({ loadingThemeContent: true });

    try {
      const { latestByTopic, latestByTheme, latestByThemeMeta, loadHighlights } = get();

      let page = get().page;
      if (!page.page) {
        page = await fetchKnowledgeHubPage();
      }

      if (!page?.page?.topics?.data) {
        throw new Error('Failed to fetch topics');
      }

      const [topicsWithLatests, themesWithLatests] = await Promise.all([
        fetchTopicsWithLatests({ topics: page.page.topics.data, topicParam, themeParam }),
        fetchThemesWithLatests({ topics: page.page.topics.data, topicParam, themeParam, pageNumber }),
      ]);

      const updatedLatestByTopic = updateLatestByTopic(latestByTopic, topicsWithLatests);
      const { updatedLatestByTheme, updatedLatestByThemeMeta } = updateLatestByTheme(
        latestByTheme,
        latestByThemeMeta,
        themesWithLatests,
      );

      const highlights = loadHighlights(page as any);

      set({
        page,
        topics: page.page.topics.data,
        highlights,
        latestByTopic: updatedLatestByTopic,
        latestByTheme: { ...updatedLatestByTheme },
        latestByThemeMeta: { ...updatedLatestByThemeMeta },
        latestResources: [],
        trendingResources: [],
        allTimeFavouriteResources: [],
        loadingThemeContent: false,
      });
    } catch (error) {
      console.error('Error initializing knowledge hub:', error);
      set({ loadingThemeContent: false });
    }
  },
  loadHighlights: (page: { page: KnowledgeHubPage | null }) => {
    return page?.page?.highlights?.data?.reduce((highlightsObj, highlight) => {
      const topic =
        highlight?.attributes?.themes?.data[0]?.attributes?.topic?.data?.attributes?.topic?.toString() || '';

      if (!highlightsObj[topic]) {
        highlightsObj[topic] = [];
      }

      highlightsObj[topic]?.push(highlight);

      return highlightsObj;
    }, {} as ResourcesMap);
  },
  loadTrendingResources: async () => {
    set({ trendingResourcesLoading: true });
    try {
      const trending = await fetchTrendingResources();
      set({
        trendingResources: trending?.resources || [],
      });
    } finally {
      set({ trendingResourcesLoading: false });
    }
  },
  onClickMoreTheme: async (theme: ThemeEntity, pageNumber: number = 1) => {
    const { latestByTheme, latestByThemeMeta } = get();
    const latests = await fetchLatestResourcesByTheme(
      theme.attributes?.theme || '',
      latestByThemeMeta[theme!.attributes!.theme!.toString()]?.meta?.pagination?.pageSize || 2,
      pageNumber,
    );

    if (!latestByTheme[theme!.attributes!.theme!.toString()]) {
      latestByTheme[theme!.attributes!.theme!.toString()] = [];
    }

    latestByThemeMeta[theme!.attributes!.theme!.toString()] = { meta: latests!.meta } as {
      meta: { pagination: { total: number; page?: number; pageCount?: number; pageSize?: number } };
    };

    latestByTheme[theme!.attributes!.theme!.toString()] = latestByTheme[theme!.attributes!.theme!.toString()]!.concat(
      latests.resorces as ResourceEntity[],
    );

    set({ latestByTheme });
  },
  filterByTheme: async (theme: string, topic: TopicEntity) => {
    const { latestByTheme } = get();

    if (compare(theme, 'null')) {
      const themePromises = (topic?.attributes?.themes?.data || []).map(async (theme) => {
        const latests = await fetchLatestResourcesByTheme(theme?.attributes?.theme || '', 2);
        return { theme: theme.attributes?.theme?.toString() || '', latests: latests.resorces };
      });

      const themesWithLatests = await Promise.all(themePromises);

      const updatedLatestByTheme = { ...latestByTheme };
      themesWithLatests.forEach(({ theme, latests }) => {
        updatedLatestByTheme[theme] = latests as ResourceEntity[];
      });

      set({ filterActive: null, latestByTheme: updatedLatestByTheme });
      return;
    }
    const latests = await fetchLatestResourcesByTheme(theme, 2);
    set({
      latestByTheme: {
        [theme]: latests.resorces as ResourceEntity[],
      },
      filterActive: theme,
    });
  },
  loadLatests: async () => {
    set({ loadingLatestsByType: true });
    const [articles, guides, toptTips] = await Promise.all([
      fetchLatestResourcesByType(['ARTICLES', 'ARTICLES & WORKSHEETS'], 3, 0),
      fetchLatestResourcesByType(['GUIDES', 'GUIDES & WORKSHEETS'], 3, 0),
      fetchLatestResourcesByType(['TOP_TIPS', 'TOP TIPS & WORKSHEETS'], 3, 0),
    ]);

    set({
      latestArticles: (articles?.resorces || []) as ResourceEntity[],
      latestGuides: (guides?.resorces || []) as ResourceEntity[],
      latestTopTips: (toptTips?.resorces || []) as ResourceEntity[],
      loadingLatestsByType: false,
    });
  },
}));

const fetchTopicsWithLatests = async ({
  topics,
  topicParam,
}: {
  topics: any[];
  topicParam?: string;
  themeParam?: string;
}) => {
  const topicPromises = topics.map(async (topic: { attributes: { topic: { toString: () => any } } }) => {
    if (topicParam && topic.attributes?.topic?.toString() !== topicParam)
      return { topic: topic?.attributes?.topic?.toString() || '', latests: [] };

    const latests = await fetchLatestResourcesByTopic(topic?.attributes?.topic?.toString() || '', 2);
    return { topic: topic?.attributes?.topic?.toString() || '', latests: latests.resorces };
  });

  return await Promise.all(topicPromises);
};

const updateLatestByTopic = (latestByTopic: ResourcesMap, topicsWithLatests: any[]) => {
  const updatedLatestByTopic = { ...latestByTopic };
  topicsWithLatests.forEach(({ topic, latests }) => {
    updatedLatestByTopic[topic] = latests;
  });
  return updatedLatestByTopic;
};

const fetchThemesWithLatests = async ({
  topics,
  topicParam,
  themeParam,
  pageNumber,
}: {
  topics: any[];
  topicParam: string | undefined;
  themeParam: string | undefined;
  pageNumber: string | null;
}) => {
  const themePromises = topics
    .filter((topic) => {
      if (!topicParam) {
        return true;
      }
      return compare(topic.attributes?.topic?.toString(), topicParam);
    })
    .flatMap(
      (topic: { attributes: { themes: { data: any[] } } }) =>
        topic.attributes?.themes?.data.map(async (theme: { attributes: { theme: { toString: () => any } } }) => {
          if (themeParam && theme.attributes?.theme?.toString() !== themeParam)
            return { theme: theme?.attributes?.theme?.toString() || '', latests: [], meta: {} };

          const latests = await fetchLatestResourcesByTheme(
            theme?.attributes?.theme?.toString() || '',
            themeParam ? 10 : 2,
            pageNumber ? parseInt(pageNumber, 10) : undefined,
          );

          return { theme: theme.attributes?.theme?.toString() || '', latests: latests.resorces, meta: latests.meta };
        }) || [],
    );

  return await Promise.all(themePromises);
};

const updateLatestByTheme = (
  latestByTheme: ResourcesMap,
  latestByThemeMeta: ResourcesMetaMap,
  themesWithLatests: any[],
) => {
  const updatedLatestByTheme = { ...latestByTheme };
  const updatedLatestByThemeMeta = { ...latestByThemeMeta };
  themesWithLatests.forEach(({ theme, latests, meta }) => {
    updatedLatestByTheme[theme] = latests as ResourceEntity[];
    updatedLatestByThemeMeta[theme] = { meta } as {
      meta: { pagination: { total: number; page?: number; pageCount?: number; pageSize?: number } };
    };
  });
  return { updatedLatestByTheme, updatedLatestByThemeMeta };
};
